import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Label, Tabs, TabsContent, TabsList, TabsTrigger, Collapsible, CollapsibleContent, CollapsibleTrigger, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@pagespeedify/ui';
import { CheckCircle, AlertTriangle, AlertOctagon, ExternalLink, ChevronDown, Filter, ArrowUpDown, Trash2, Edit, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../lib/apiClient';
import { useNotificationDeleteQueue } from '../components/notifications/NotificationDeleteQueueContext';
import NotificationDeleteQueuePanel from '../components/notifications/NotificationDeleteQueuePanel';
import {
  isPushNotificationSupported,
  getNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from '../lib/pushNotifications';
import '../styles/reports.css';
import { useAuth } from '../lib/auth/AuthContext';
import { getUserPlanType } from '../utils/monitoring';

type NotificationItem = {
  id: string;
  subject: string;
  body: string;
  read_at: string | null;
  sent_at: string;
  related_entity_type?: string;
  related_entity_id?: string;
};

type ParsedMetric = {
  name: string;
  value: number;
  unit: string;
  threshold: number;
  severity: 'good' | 'improvement' | 'fail';
};

type ParsedNotification = {
  url: string;
  metrics: ParsedMetric[];
  rawBody: string;
};

type NotificationPreferences = {
  enabled: boolean;
  channels: {
    email: boolean;
    frontend: boolean;
    push: boolean;
  };
  email: {
    frequency: 'immediate' | 'daily_digest' | 'weekly_digest';
  };
  push: {
    subscription: PushSubscription | null;
  };
};

type NotificationsResponse = {
  success: boolean;
  data: NotificationItem[];
  preferences?: Partial<NotificationPreferences>;
};

const defaultPreferences: NotificationPreferences = {
  enabled: true,
  channels: {
    email: true,
    frontend: true,
    push: false,
  },
  email: {
    frequency: 'daily_digest',
  },
  push: {
    subscription: null,
  },
};

const normalizePreferences = (raw?: Partial<NotificationPreferences>): NotificationPreferences => ({
  enabled: raw?.enabled !== false,
  channels: {
    email: raw?.channels?.email !== false,
    frontend: raw?.channels?.frontend !== false,
    push: raw?.channels?.push === true,
  },
  email: {
    frequency: raw?.email?.frequency || 'daily_digest',
  },
  push: {
    subscription: raw?.push?.subscription || null,
  },
});

const NotificationsPage: React.FC = () => {
  const apiClient = useApi();
  const queryClient = useQueryClient();
  const { addNotificationToDeleteQueue, isDeleteQueueVisible } = useNotificationDeleteQueue();
  const tabTriggerClasses = 'w-full justify-start rounded-md px-3 py-2 text-sm font-medium transition-colors data-[state=active]:bg-muted data-[state=active]:text-foreground hover:bg-muted/70';

  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences);
  const [readFilter, setReadFilter] = useState<'all' | 'read' | 'unread'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [typeFilter, setTypeFilter] = useState<'all' | 'monitoring'>('all');
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');

  // Parse notification body to extract URL, metrics, and thresholds
  const parseNotification = (body: string): ParsedNotification => {
    // Extract URL
    const urlMatch = body.match(/for (https?:\/\/[^\s:,]+)/i);
    const url = urlMatch ? urlMatch[1] : '';

    // Remove performance_score from the message
    let cleanBody = body.replace(/,?\s*performance_score of [\d.]+ is (?:above|below) the threshold of [\d.]+,?\s*/gi, '');
    cleanBody = cleanBody.replace(/,\s*,/g, ',').replace(/:\s*,/g, ':').replace(/,\s*$/g, '');

    const metrics: ParsedMetric[] = [];

    // Parse each metric breach: "metric of value is above/below the threshold of threshold_value"
    const metricPattern = /(\w+) of ([\d.]+)(?:s)?\s+is\s+(above|below)\s+the\s+threshold\s+of\s+([\d.]+)(?:s)?/gi;
    let match;

    while ((match = metricPattern.exec(cleanBody)) !== null) {
      const [fullMatch, metricKey, valueStr, direction, thresholdStr] = match;
      const metricLower = metricKey.toLowerCase();

      // Skip performance_score
      if (metricLower === 'performance_score') continue;

      let value = parseFloat(valueStr);
      let threshold = parseFloat(thresholdStr);
      let unit = '';

      const metricNames: Record<string, string> = {
        'lcp': 'LCP',
        'fcp': 'FCP',
        'tti': 'TTI',
        'tbt': 'TBT',
        'cls': 'CLS',
        'ttfb': 'TTFB',
        'speed_index': 'Speed Index',
        'inp': 'INP'
      };

      const name = metricNames[metricLower] || metricKey.toUpperCase();

      // Convert milliseconds to seconds for time metrics (all timing metrics are stored in ms)
      if (['tti', 'tbt', 'fcp', 'lcp', 'ttfb', 'speed_index', 'inp'].includes(metricLower)) {
        value = value / 1000;
        threshold = threshold / 1000;
        unit = 's';
      }

      // Determine decimals and format cleanBody
      const decimals = metricLower === 'cls' ? 3 : 2;
      const formattedValue = value.toFixed(decimals);
      const formattedThreshold = threshold.toFixed(decimals);
      const formattedMatch = `${metricKey} of ${formattedValue}${unit} is ${direction} the threshold of ${formattedThreshold}${unit}`;
      cleanBody = cleanBody.replace(fullMatch, formattedMatch);

      // Determine severity based on how far over threshold
      const difference = direction === 'above'
        ? ((value - threshold) / threshold) * 100
        : ((threshold - value) / threshold) * 100;

      let severity: 'good' | 'improvement' | 'fail';
      if (difference < 10) {
        severity = 'improvement'; // Within 10% of threshold
      } else if (difference >= 10 && difference < 30) {
        severity = 'improvement'; // 10-30% over
      } else {
        severity = 'fail'; // >30% over
      }

      metrics.push({ name, value, unit, threshold, severity });
    }

    return { url, metrics, rawBody: cleanBody };
  };

  // Group notifications by date
  const groupNotificationsByDate = (notifications: NotificationItem[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups: Record<string, NotificationItem[]> = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Older': []
    };

    notifications.forEach(notification => {
      const notifDate = new Date(notification.sent_at);
      const notifDateOnly = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

      if (notifDateOnly.getTime() === today.getTime()) {
        groups['Today'].push(notification);
      } else if (notifDateOnly.getTime() === yesterday.getTime()) {
        groups['Yesterday'].push(notification);
      } else if (notifDateOnly >= weekAgo) {
        groups['This Week'].push(notification);
      } else {
        groups['Older'].push(notification);
      }
    });

    return groups;
  };

  // Get relative time string
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  // Get severity icon with color
  const getSeverityIcon = (severity: 'good' | 'improvement' | 'fail') => {
    switch (severity) {
      case 'good':
        return <CheckCircle className="h-5 w-5 shrink-0 text-report-pass" />;
      case 'improvement':
        return <AlertTriangle className="h-5 w-5 shrink-0 text-report-improvement" />;
      case 'fail':
        return <AlertOctagon className="h-5 w-5 shrink-0 text-report-fail" />;
    }
  };

  // Determine overall severity of notification
  const getOverallSeverity = (metrics: ParsedMetric[]): 'good' | 'improvement' | 'fail' => {
    if (metrics.some(m => m.severity === 'fail')) return 'fail';
    if (metrics.some(m => m.severity === 'improvement')) return 'improvement';
    return 'good';
  };

  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await apiClient.get<NotificationsResponse>('/notifications');
      if (!response?.success) {
        throw new Error('Unable to load notifications');
      }
      return response;
    },
  });

  const notifications = notificationsQuery.data?.data ?? [];
  const isLoading = notificationsQuery.isLoading;
  const hasError = notificationsQuery.isError;

  useEffect(() => {
    if (notificationsQuery.data?.preferences) {
      setPreferences(normalizePreferences(notificationsQuery.data.preferences));
    } else if (notificationsQuery.isSuccess) {
      setPreferences(defaultPreferences);
    }
  }, [notificationsQuery.data, notificationsQuery.isSuccess]);

  // Ensure selected email frequency is valid for the user's plan.
  // If the server returned a frequency that the current plan doesn't allow,
  // update the UI to a valid default (first allowed) without saving it.
  const planType = getUserPlanType(undefined, user?.user_type);
  const isEnterprise = planType === 'enterprise' || String(planType || '').toLowerCase() === 'enterprise';
  const isPro = planType === 'pro' || String(planType || '').toLowerCase() === 'pro';
  const isBasic = planType === 'basic' || String(planType || '').toLowerCase() === 'basic';

  const allowedFrequencies = React.useMemo(() => {
    if (isEnterprise) return ['immediate', 'daily_digest', 'weekly_digest'];
    if (isPro) return ['daily_digest', 'weekly_digest'];
    return ['weekly_digest'];
  }, [isEnterprise, isPro]);

  useEffect(() => {
    // If the UI currently shows a frequency not allowed, coerce to first allowed value
    if (preferences?.email?.frequency && !allowedFrequencies.includes(preferences.email.frequency)) {
      setPreferences((prev) => ({
        ...prev,
        email: { frequency: allowedFrequencies[0] as any },
      }));
    }
  }, [allowedFrequencies, preferences?.email?.frequency]);

  // Check push notification support
  useEffect(() => {
    const checkPushSupport = () => {
      const isSupported = isPushNotificationSupported();
      setPushSupported(isSupported);

      if (isSupported) {
        setPushPermission(getNotificationPermission());
      }
    };

    checkPushSupport();
  }, []);

  // Handle push notification toggle
  const handlePushToggle = async (enabled: boolean) => {
    if (enabled) {
      try {
        const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
        if (!vapidPublicKey) {
          throw new Error('VAPID public key not configured');
        }

        const subscription = await subscribeToPushNotifications(vapidPublicKey);
        const newPrefs = {
          ...preferences,
          channels: { ...preferences.channels, push: true },
          push: { subscription: subscription as any },
        };
        setPreferences(newPrefs);
        setPushPermission(getNotificationPermission());

        // Auto-save to backend immediately (don't require user to click Save)
        updatePreferencesMutation.mutate({ notifications: newPrefs });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        toast.error(`Failed to enable push notifications: ${errorMessage}`);
        setPreferences(prev => ({
          ...prev,
          channels: { ...prev.channels, push: false },
        }));
      }
    } else {
      try {
        await unsubscribeFromPushNotifications();
        const newPrefs = {
          ...preferences,
          channels: { ...preferences.channels, push: false },
          push: { subscription: null },
        };
        setPreferences(newPrefs);

        // Auto-save to backend immediately
        updatePreferencesMutation.mutate({ notifications: newPrefs });
      } catch (error) {
        toast.error('Failed to disable push notifications');
      }
    }
  };

  const markAllAsReadMutation = useMutation({
    mutationFn: () => apiClient.delete('/notifications'),
    onSuccess: () => {
      toast.success('All notifications marked as read');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] });
    },
    onError: () => toast.error('Failed to mark all as read'),
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] });
    },
    onError: () => toast.error('Failed to mark notification as read'),
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (payload: { notifications: NotificationPreferences }) =>
      apiClient.put('/notifications/preferences', payload),
    onSuccess: () => {
      toast.success('Preferences saved');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: () => toast.error('Unable to save preferences'),
  });

  const handleSavePreferences = () => {
    let coercedPreferences = { ...preferences };
    if (isBasic) {
      coercedPreferences.email.frequency = 'weekly_digest';
    }
    updatePreferencesMutation.mutate({ notifications: coercedPreferences });
  };

  const allNotificationsRead = notifications.length === 0 || notifications.every((notification) => notification.read_at);

  // Filter notifications based on read status and type
  const filteredNotifications = notifications.filter(notification => {
    // Filter by read status
    if (readFilter === 'read' && !notification.read_at) return false;
    if (readFilter === 'unread' && notification.read_at) return false;

    // Filter by type
    if (typeFilter === 'monitoring' && notification.related_entity_type !== 'monitoring') return false;

    return true;
  });

  // Sort notifications
  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    const dateA = new Date(a.sent_at).getTime();
    const dateB = new Date(b.sent_at).getTime();
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  const groupedNotifications = groupNotificationsByDate(sortedNotifications);

  return (
    <>
      {isDeleteQueueVisible && <NotificationDeleteQueuePanel />}
      <div className="p-4 sm:p-6">
        <Tabs defaultValue="notifications" orientation="vertical" className="flex flex-col gap-6 md:flex-row">
          <div className="flex w-full shrink-0 flex-col gap-2 md:w-52">
            <TabsList className="flex h-auto w-full flex-col gap-1 bg-transparent p-0">
              <TabsTrigger value="notifications" className={tabTriggerClasses}>
                Notifications
              </TabsTrigger>
              <TabsTrigger value="preferences" className={tabTriggerClasses}>
                Preferences
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1">
            <TabsContent value="notifications" className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-3xl font-bold">Notifications</h1>
                <div className="flex flex-wrap gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline">
                        <Filter className="mr-2 h-4 w-4" />
                        Type: {typeFilter === 'all' ? 'All' : 'Monitoring'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setTypeFilter('all')}>
                        All Types
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTypeFilter('monitoring')}>
                        Monitoring Only
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline">
                        <Filter className="mr-2 h-4 w-4" />
                        Status: {readFilter === 'all' ? 'All' : readFilter === 'unread' ? 'Unread' : 'Read'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setReadFilter('all')}>
                        All Notifications
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setReadFilter('unread')}>
                        Unread Only
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setReadFilter('read')}>
                        Read Only
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline">
                        <ArrowUpDown className="mr-2 h-4 w-4" />
                        Sort: {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setSortOrder('newest')}>
                        Newest First
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortOrder('oldest')}>
                        Oldest First
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {hasError ? (
                <Card>
                  <CardContent className="flex flex-col gap-3 py-8 text-center">
                    <p className="text-sm text-muted-foreground">We couldn't load notifications.</p>
                    <Button size="sm" onClick={() => notificationsQuery.refetch()}>
                      Try again
                    </Button>
                  </CardContent>
                </Card>
              ) : isLoading ? (
                <p>Loading...</p>
              ) : notifications.length === 0 ? (
                <Card>
                  <CardContent className="py-10 text-center text-sm text-muted-foreground">
                    You're all caught up. New alerts will show up here.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedNotifications).map(([groupName, groupNotifications]) => {
                    if (groupNotifications.length === 0) return null;

                    return (
                      <div key={groupName} className="space-y-4">
                        <h2 className="text-xl font-semibold text-foreground">{groupName}</h2>

                        {groupNotifications.map((notification) => {
                          const parsed = parseNotification(notification.body);
                          const severity = getOverallSeverity(parsed.metrics);
                          const isRead = !!notification.read_at;

                          return (
                            <div
                              key={notification.id}
                              className={`relative rounded-xl border transition-all border-l-4 ${!isRead
                                  ? 'border-primary/20 bg-muted shadow-md hover:bg-muted/80'
                                  : 'border-border bg-card hover:bg-card/80'
                                } ${severity === 'good' ? 'border-l-report-pass' :
                                  severity === 'improvement' ? 'border-l-report-improvement' :
                                    'border-l-report-fail'
                                }`}
                            >
                              {/* Action icons in top right */}
                              <div className="absolute top-3 right-3 z-10 flex gap-1">
                                {!isRead && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => markAsReadMutation.mutate(notification.id)}
                                          disabled={markAsReadMutation.isPending}
                                          className="h-8 w-8 p-0 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                                        >
                                          <Check className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Mark Read</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {notification.related_entity_type === 'monitoring' && notification.related_entity_id && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          asChild
                                          className="h-8 w-8 p-0 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                                        >
                                          <Link to={`/monitoring`} state={{ editingTask: { id: notification.related_entity_id, source_id: notification.related_entity_id, type: 'monitoring' } }}>
                                            <Edit className="h-4 w-4" />
                                          </Link>
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Edit Alert</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          const success = addNotificationToDeleteQueue({
                                            id: notification.id,
                                            subject: notification.subject
                                          });
                                          if (success) {
                                            toast.success('Notification added to delete queue');
                                          } else {
                                            toast.info('Notification already in delete queue');
                                          }
                                        }}
                                        className="h-8 w-8 p-0 text-muted-foreground hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950 dark:hover:text-red-400"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>

                              <div className="flex gap-4 p-5 pr-12">
                                {/* Status Icon */}
                                <div className="shrink-0">
                                  {getSeverityIcon(severity)}
                                </div>

                                <div className="flex-1 space-y-3">
                                  {/* Title and URL */}
                                  <div>
                                    <h3 className="text-lg font-semibold text-foreground">{notification.subject}</h3>
                                    <div className="flex items-center gap-1">
                                      <a
                                        href={parsed.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                                      >
                                        {parsed.url}
                                      </a>
                                      <a
                                        href={parsed.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-muted-foreground hover:text-foreground"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                      </a>
                                    </div>
                                  </div>

                                  {/* Metric Badges */}
                                  <div className="flex flex-wrap items-center gap-2">
                                    {parsed.metrics.map((metric, idx) => {
                                      const badgeClass =
                                        metric.severity === 'good' ? 'badge-report-pass' :
                                          metric.severity === 'improvement' ? 'badge-report-improvement' :
                                            'badge-report-fail';

                                      const decimals = metric.name === 'CLS' ? 3 : 2;

                                      return (
                                        <span
                                          key={idx}
                                          className={`px-3 py-1 rounded-lg text-sm font-medium ${badgeClass}`}
                                        >
                                          {metric.name} {metric.value.toFixed(decimals)}{metric.unit}
                                        </span>
                                      );
                                    })}

                                    {parsed.metrics.length > 0 && parsed.metrics[0].threshold && (
                                      <span className="px-3 py-1 rounded-lg text-sm font-medium bg-secondary text-secondary-foreground">
                                        Threshold {parsed.metrics[0].threshold.toFixed(parsed.metrics[0].name === 'CLS' ? 3 : 2)}{parsed.metrics[0].unit}
                                      </span>
                                    )}

                                    <span className="ml-auto text-sm text-muted-foreground">
                                      {getRelativeTime(new Date(notification.sent_at))}
                                    </span>
                                  </div>

                                  {/* Collapsible Details */}
                                  <Collapsible>
                                    <CollapsibleTrigger asChild>
                                      <Button variant="ghost" size="sm" className="gap-2 text-foreground px-0 hover:bg-transparent hover:text-foreground cursor-pointer">
                                        <ChevronDown className="h-4 w-4" />
                                        Show Details
                                      </Button>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-3 rounded-lg bg-muted/50 p-4">
                                      <p className="text-sm text-foreground">{parsed.rawBody}</p>
                                      {parsed.metrics.map((metric, idx) => {
                                        const decimals = metric.name === 'CLS' ? 3 : 2;
                                        return (
                                          <div key={idx} className="mt-2 flex justify-between text-sm">
                                            <span className="text-muted-foreground">{metric.name}:</span>
                                            <span className="text-foreground">
                                              {metric.value.toFixed(decimals)}{metric.unit} (threshold: {metric.threshold.toFixed(decimals)}{metric.unit})
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </CollapsibleContent>
                                  </Collapsible>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="preferences">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Master Toggle */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label className="font-medium">Enable notifications</Label>
                      <p className="text-sm text-muted-foreground">Turn off to pause all alert delivery.</p>
                    </div>
                    <Checkbox
                      checked={preferences.enabled}
                      onCheckedChange={(checked) => {
                        setPreferences((prev) => ({
                          ...prev,
                          enabled: checked === true,
                        }));
                      }}
                      className="h-4 w-4 shrink-0 rounded-sm border-2 bg-transparent border-white/40 dark:border-white/40 focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary"
                    />
                  </div>

                  {/* Notification Channels */}
                  <div className="space-y-3">
                    <Label className="font-medium">Channels</Label>

                    {/* Email Channel */}
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <p className="text-sm font-medium">Email</p>
                        <p className="text-xs text-muted-foreground">Send alerts to your email address.</p>
                      </div>
                      <Checkbox
                        checked={preferences.channels.email}
                        onCheckedChange={(checked) =>
                          setPreferences((prev) => ({
                            ...prev,
                            channels: { ...prev.channels, email: checked === true },
                          }))
                        }
                        disabled={!preferences.enabled}
                        className="h-4 w-4 shrink-0 rounded-sm border-2 bg-transparent border-white/40 dark:border-white/40 focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary"
                      />
                    </div>

                    {/* Email Frequency Settings */}
                    {preferences.channels.email && (isEnterprise || isPro) && (
                      <div className="ml-4 space-y-3 border-l-2 border-muted pl-4">
                        <div>
                          <Label className="text-sm font-medium">Email Frequency</Label>
                          <p className="text-xs text-muted-foreground mb-2">
                            Choose how often to receive monitoring alerts
                          </p>
                          <select
                            value={preferences.email.frequency}
                            onChange={(e) =>
                              setPreferences((prev) => ({
                                ...prev,
                                email: { frequency: e.target.value as any },
                              }))
                            }
                            disabled={!preferences.enabled}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isEnterprise && <option value="immediate">Immediate (hourly batches, Enterprise only)</option>}
                            <option value="daily_digest">Daily Digest (once per day)</option>
                            <option value="weekly_digest">Weekly Digest (once per week)</option>
                          </select>

                          {/* Frequency Descriptions */}
                          {preferences.email.frequency === 'immediate' && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              ⏱️ Checks every hour.Multiple breaches aggregated into one in one email.
                            </p>
                          )}
                          {preferences.email.frequency === 'daily_digest' && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              📅 Sent once per day. All breaches aggregated into one email. 24h cooldown.
                            </p>
                          )}
                          {preferences.email.frequency === 'weekly_digest' && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              📆 Sent once per week. All breaches aggregated into one email..
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* In-app Channel */}
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <p className="text-sm font-medium">In-app</p>
                        <p className="text-xs text-muted-foreground">Show alerts inside Swayashin.</p>
                      </div>
                      <Checkbox
                        checked={preferences.channels.frontend}
                        onCheckedChange={(checked) =>
                          setPreferences((prev) => ({
                            ...prev,
                            channels: { ...prev.channels, frontend: checked === true },
                          }))
                        }
                        disabled={!preferences.enabled}
                        className="h-4 w-4 shrink-0 rounded-sm border-2 bg-transparent border-white/40 dark:border-white/40 focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary"
                      />
                    </div>

                    {/* Push Notifications Channel */}
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div>
                        <p className="text-sm font-medium">Push Notifications</p>
                        <p className="text-xs text-muted-foreground">
                          {!pushSupported
                            ? 'Not supported in your browser'
                            : pushPermission === 'denied'
                              ? 'Permission denied. Please enable in browser settings.'
                              : 'Receive alerts even when the app is closed.'}
                        </p>
                      </div>
                      <Checkbox
                        checked={preferences.channels.push}
                        onCheckedChange={(checked) => handlePushToggle(checked === true)}
                        disabled={!preferences.enabled || !pushSupported || pushPermission === 'denied'}
                        className="h-4 w-4 shrink-0 rounded-sm border-2 bg-transparent border-white/40 dark:border-white/40 focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button size="sm" onClick={handleSavePreferences} disabled={updatePreferencesMutation.isPending}>
                      {updatePreferencesMutation.isPending ? 'Saving...' : 'Save preferences'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );
};

export default NotificationsPage;